import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import router from '@/router'

interface User {
  id: number
  email: string
  name: string
  role?: string
  account_status?: string
}

interface LoginCredentials {
  email: string
  password: string
}

interface RegisterData {
  first_name: string
  middle_name?: string | null
  last_name: string
  email: string
  phone_number?: string | null
  address?: string | null
  password: string
  password_confirmation: string
}

interface AuthResponse {
  message?: string
  token?: string
  user: User
  account_status?: string
}

interface ApiError {
  message: string
  status: number
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('token'))
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const currentUser = computed(() => user.value)
  const isLoading = computed(() => loading.value)
  const hasError = computed(() => !!error.value)

  // Actions
  async function login(credentials: LoginCredentials) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post<AuthResponse>('/auth/login', credentials)
      const { token: newToken, user: userData } = response.data
      
      // Check if account is approved
      if (userData.account_status && userData.account_status !== 'approved') {
        throw new Error(response.data.message || 'Your account is not yet approved')
      }
      
      if (!newToken) {
        throw new Error('No token received from server')
      }
      
      token.value = newToken
      user.value = userData
      
      localStorage.setItem('token', newToken)
      
      if (userData.role === 'admin') {
        router.push('/')
      } else {
        router.push('/materials')
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to login. Please try again.'
      error.value = errorMessage
      throw err
    } finally {
      loading.value = false
    }
  }

  async function register(data: RegisterData) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post<AuthResponse>('/auth/register', data)
      
      // Don't save token or redirect to home - account is pending approval
      // Just return success so the composable can show the message
      return response.data
    } catch (err: any) {
      // Extract validation errors if available
      if (err.response?.data?.errors) {
        const validationErrors = Object.values(err.response.data.errors).flat().join(', ')
        error.value = validationErrors || 'Validation failed'
      } else {
        const apiError = err as ApiError
        error.value = apiError.message || 'Failed to register. Please try again.'
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    loading.value = true
    error.value = null

    try {
      await api.post('/auth/logout')
    } catch (err) {
      const apiError = err as ApiError
      error.value = apiError.message || 'Failed to logout. Please try again.'
      console.error('Logout error:', err)
    } finally {
      token.value = null
      user.value = null
      localStorage.removeItem('token')
      router.push('/login')
      loading.value = false
    }
  }

  async function checkAuth() {
    if (!token.value) return false

    // If we already have a user, no need to check again
    if (user.value) return true

    loading.value = true
    error.value = null

    try {
      // Validate token with backend
      const response = await api.get<User>('/auth/me')
      user.value = response.data
      return true
    } catch (err) {
      // Token is invalid or expired
      error.value = 'Session expired. Please login again.'
      token.value = null
      user.value = null
      localStorage.removeItem('token')
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Set authentication state directly.
   * Used by mock flows and programmatic auth restoration.
   * Prefer `login()` for real authentication.
   */
  function setAuth(newToken: string, userData: User) {
    token.value = newToken
    user.value = userData
    localStorage.setItem('token', newToken)
  }

  function clearError() {
    error.value = null
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    // Getters
    isAuthenticated,
    currentUser,
    isLoading,
    hasError,
    // Actions
    login,
    register,
    logout,
    checkAuth,
    setAuth,
    clearError
  }
}) 